import { useState, useEffect, useCallback } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { useWizard } from '@/components/common/wizard/WizardContext'
import { GET_AUDIENCES_WITH_FILTERS } from '@/graphql/audience-queries'
import { FilterSection } from './FilterSection'
import { AudiencesList } from './AudiencesList'
import { Footer } from './Footer'
import useAudienceSelectionStore from '@/store/audienceSelectionStore'
import { isAudienceExcluded } from '@/utils/ad-campaigns-helpers'

const AttachAudience = () => {
    const { id: campaignId } = useParams()
    const {
        getStepContentData,
        setStepContentData,
        goToStep,
        setVisible,
        registerCustomNavigation,
        clearCustomNavigation,
        removeStepContentData,
    } = useWizard()
    const [searchDebounceTimer, setSearchDebounceTimer] = useState(null)

    // Get funnel_stage from wizard navigation state
    const navigationData = getStepContentData('attachAudience') || {}
    const { funnel_stage } = navigationData
    const [selectedStages, setSelectedStages] = useState([funnel_stage])

    // Get existing selections from Sales Funnel Setup
    const salesFunnelData = getStepContentData('salesFunnelSetup') || {}
    const [existingSelections] = useState(salesFunnelData.audienceSelections || {})

    // Get pixel status from campaign objective step
    const campaignObjectiveData = getStepContentData('campaignObjective') || {}
    const pixelIsActive = !!campaignObjectiveData?.pixelIsValid

    const {
        isLoading,
        searchTerm,
        selectedIds,
        setAudiences,
        setLoading,
        setSearchTerm,
        toggleAudienceSelection,
        selectAll,
        initializeSelection,
        getFilteredAudiences,
        clearFunnelStageFilters,
    } = useAudienceSelectionStore()

    // Reset funnel stage filters when this step is loaded
    useEffect(() => clearFunnelStageFilters(), [])

    // Initialize with existing selections for this funnel stage
    useEffect(() => {
        const existingIds = existingSelections[funnel_stage] || []
        initializeSelection(existingIds)
    }, [funnel_stage])

    const [fetchAudiences] = useLazyQuery(GET_AUDIENCES_WITH_FILTERS, {
        onCompleted: (data) => {
            const audiences = data?.Audience?.audiences || []
            setAudiences(audiences)
            setLoading(false)
        },
        onError: (error) => {
            console.error('Error fetching audiences:', error)
            setLoading(false)
        },
    })

    const filterAudiences = () => {
        const searchQuery = searchTerm.length >= 2 ? searchTerm : null

        // Only show loading if we don't have existing audiences
        if (getFilteredAudiences().length === 0) {
            setLoading(true)
        }

        fetchAudiences({
            variables: {
                funnel_stages: selectedStages,
                searchTerm: searchQuery,
                active: true,
                campaignId: campaignId,
            },
        })
    }

    // Debounced search and filter effect
    useEffect(() => {
        if (searchDebounceTimer) clearTimeout(searchDebounceTimer)

        const timer = setTimeout(() => {
            filterAudiences()
        }, 300)

        setSearchDebounceTimer(timer)
        return () => timer && clearTimeout(timer)
    }, [searchTerm])

    const handleAttachSelected = useCallback(() => {
        // Update Sales Funnel Setup data with selected audiences
        const ids = Array.from(selectedIds)
        const selectedAudiences = filteredAudiences.filter(
            (audience) => ids.includes(audience.id) && !isAudienceExcluded(audience, pixelIsActive),
        )
        const updatedSelections = { ...existingSelections }

        selectedAudiences.forEach((audience) => {
            // Ensure the funnel stage array exists
            if (!updatedSelections[audience.funnel_stage]) {
                updatedSelections[audience.funnel_stage] = []
            }

            // Fix: arrays contain IDs directly, not objects with .id property
            const hasAudience = updatedSelections[audience.funnel_stage].includes(audience.id)

            if (!hasAudience) {
                updatedSelections[audience.funnel_stage].push(audience.id)
            }
        })

        // Deduplicate all arrays to ensure uniqueness
        Object.keys(updatedSelections).forEach((stageId) => {
            updatedSelections[stageId] = [...new Set(updatedSelections[stageId])]
        })

        setStepContentData({ audienceSelections: updatedSelections }, 'salesFunnelSetup')
        removeStepContentData('budgetSchedule')

        setVisible('attachAudience', false)
        goToStep('salesFunnelSetup')
    }, [
        selectedIds,
        funnel_stage,
        existingSelections,
        setStepContentData,
        goToStep,
        setVisible,
        selectedStages,
        pixelIsActive,
    ])

    const handleCancel = useCallback(() => {
        // Don't save any changes, just go back
        setVisible('attachAudience', false)
        goToStep('salesFunnelSetup')
    }, [setVisible, goToStep])

    const filteredAudiences = getFilteredAudiences()

    // Filter out excluded audiences from selection count
    const validSelectedAudiences = filteredAudiences.filter(
        (audience) => selectedIds.has(audience.id) && !isAudienceExcluded(audience, pixelIsActive),
    )
    const validSelectionCount = validSelectedAudiences.length

    // Check if any selected audiences are excluded due to pixel
    const hasExcludedAudiences = Array.from(selectedIds).some((id) => {
        const audience = filteredAudiences.find((a) => a.id === id)
        return audience && isAudienceExcluded(audience, pixelIsActive)
    })

    // Register custom navigation
    useEffect(() => {
        const customNav = (
            <Footer
                selectionCount={validSelectionCount}
                onAttachSelected={handleAttachSelected}
                onCancel={handleCancel}
                hasExcludedAudiences={hasExcludedAudiences}
            />
        )
        registerCustomNavigation(customNav)

        return () => clearCustomNavigation()
    }, [validSelectionCount, hasExcludedAudiences])

    const handleStageSelection = (stage) => {
        setSelectedStages((prev) => {
            if (prev.includes(stage)) {
                return prev.filter((s) => s !== stage)
            }
            return [...prev, stage]
        })
    }

    return (
        <div className="tw:bg-[#071727] tw:h-full tw:text-[#c9d1d9] tw:flex tw:flex-col tw:overflow-hidden">
            <div className="tw:flex-1 tw:flex tw:flex-col tw:overflow-hidden tw:min-h-0">
                <div className="tw:flex-shrink-0 tw:p-6">
                    <FilterSection
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        selectedStages={selectedStages}
                        handleApplyFilter={filterAudiences}
                        currentFunnelStage={funnel_stage}
                        onSelectAll={() => selectAll(filteredAudiences)}
                        onStageSelection={handleStageSelection}
                        restrictToFunnelStages={[funnel_stage]}
                    />
                </div>

                <div className="tw:flex-1 tw:overflow-auto tw:px-6 tw:min-h-0">
                    <AudiencesList
                        isLoading={isLoading}
                        audiences={filteredAudiences}
                        selectedIds={selectedIds}
                        onToggleSelection={toggleAudienceSelection}
                        pixelIsActive={pixelIsActive}
                    />
                </div>
            </div>
        </div>
    )
}

AttachAudience.displayName = 'AttachAudience'

export default AttachAudience
